// filepath: src/access/AccessContext.jsx
// PURPOSE: Central, canonical "acting access" context.
// - If manager mode is active (via lib/managerMode), we act as the INVITEE owner (read-only).
// - Otherwise we act as OURSELVES (read/write if subscribed).

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";

// Canonical manager-mode helpers
import {
  sanitizeBoot,            // -> { ownerUid, managerUid, grantId, ts } | null
  onManagerModeChange,     // subscribe to changes; returns unsubscribe
  setManagerMode,          // set(ownerUid, managerUid, grantId?)
  clearAllManagerTraces,   // clears all manager-mode keys (incl. canonical "lwb:managerMode")
  setHardblock,            // prevent auto re-entry this session
  isReadOnly as mmIsReadOnly,
} from "../lib/managerMode";

// ---- Cleanup on first load (legacy + canonical key) ----
function cleanupAllKeys() {
  const legacyKeys = [
    "managedOwnerUid", "readOnly", "readonly", "managerMode",
    "managerUid", "ownerUid",
  ];
  try {
    for (const k of legacyKeys) {
      try { localStorage.removeItem(k); } catch {}
      try { sessionStorage.removeItem(k); } catch {}
    }

  } catch {}
}

const AccessCtx = createContext(null);

export function AccessProvider({ children }) {
  const [state, setState] = useState({
    ready: false,
    user: null,      // Firebase user (viewer)
    ownerUid: null,  // ACTING uid for data (invitee owner when in manager mode; else self)
    readOnly: false, // true only when in manager mode
    manager: false,  // viewer has any active grants?
    manages: [],     // list of owner UIDs this viewer can manage
  });

  // One-time on mount
  useEffect(() => { cleanupAllKeys(); }, []);

  // React to Firebase auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      const path =
        (typeof window !== "undefined" && window.location && window.location.pathname) || "/";
      const isAcceptFlow = path === "/accept-invitation" || path.startsWith("/am/accept");

      if (!fbUser) {
        setState({
          ready: true, user: null, ownerUid: null,
          readOnly: false, manager: false, manages: [],
        });
        return;
      }

      // 🔧 NEW: on every login (unless we're on accept-invite), nuke stale onboarding/manager flags
      let mm = sanitizeBoot();
      if (!isAcceptFlow) {
        try { sessionStorage.removeItem("amOnboarding"); } catch {}
        if (!mm || mm.managerUid !== fbUser.uid) {
          try { clearAllManagerTraces(); } catch {}
          mm = null;
        }
      }

      // Fetch active grants (viewer manages who?)
      let manages = [];
      try {
        const qGrants = query(
          collection(db, "grants"),
          where("managerUid", "==", fbUser.uid),
          where("active", "==", true)
        );
        const snap = await getDocs(qGrants);
        manages = snap.docs.map((d) => d.data()?.ownerUid).filter(Boolean);
      } catch {
        manages = [];
      }

      // Decide acting context from canonical managerMode — NOT legacy storage
      
      const mmActive = !!mm && mm.managerUid === fbUser.uid && !!mm.ownerUid;

      setState({
        ready: true,
        user: fbUser,
        ownerUid: mmActive ? mm.ownerUid : fbUser.uid,
        readOnly: mmActive,
        manager: manages.length > 0,
        manages,
      });
    });
    return () => unsub();
  }, []);

  // React to manager-mode changes (enter/exit/other tab)
  useEffect(() => {
    const unsub = onManagerModeChange(() => {
      setState((prev) => {
        const fbUser = auth.currentUser || prev.user;
        if (!fbUser) return { ...prev, user: null, ownerUid: null, readOnly: false };
        const mm = sanitizeBoot();
        const mmActive = !!mm && mm.managerUid === fbUser.uid && !!mm.ownerUid;
        return {
          ...prev,
          user: fbUser,
          ownerUid: mmActive ? mm.ownerUid : fbUser.uid,
          readOnly: mmActive,
        };
      });
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, []);

  // Public API (preserve your previous method names; now using canonical managerMode)
  const api = useMemo(() => ({
    ...state,

    // Choose a managed owner to act as (read-only). This *enters* manager mode.
    setManagedOwnerUid: (ownerUid, grantId = null) => {
      const viewer = auth.currentUser || state.user;
      if (!viewer || !ownerUid) return;
      try {
        setManagerMode(ownerUid, viewer.uid, grantId || null);
      } catch {}
      // Hard reload so all listeners rebind under the acting uid
      window.location.assign("/manager");
    },

    // Exit managed mode and go back to our own writable account
    clearManagedOwner: () => {
      try {
        setHardblock(true);       // prevent auto re-entry this session
        clearAllManagerTraces();  // nuke any legacy/canonical flags too
        document.body.classList.remove("lwb-readonly");
      } catch {}
      window.location.assign("/"); // hard reload to reset context
    },

    // Convenience mirrors for consumers that want a boolean
    get isReadOnly() {
      return mmIsReadOnly();
    },
  }), [state]);

  return <AccessCtx.Provider value={api}>{children}</AccessCtx.Provider>;
}

export function useAccess() {
  return useContext(AccessCtx);
}

/**
 * Optional bootstrap. Previous behavior auto-redirected when multiple grants existed.
 * We now ONLY redirect if a (rare) malformed managerMode exists without ownerUid.
 * Otherwise user lands in their own account; switching happens explicitly.
 */
export function AccessBootstrap() {
  let navigate;
  try {
    // eslint-disable-next-line global-require
    navigate = require("react-router-dom").useNavigate?.();
  } catch { /* router not available */ }

  const { ready } = useAccess();

  useEffect(() => {
    if (!ready) return;
    const mm = sanitizeBoot();
    if (mm && !mm.ownerUid && navigate) {
      navigate("/manager-select", { replace: true });
    }
  }, [ready, navigate]);

  return null;
}
